// =================================================================================================
// Template USAGE:
//  - Update AmChartMapWithTimelineData with widget settings
//  - Update AmChartMapWithTimelineSettings with mapping of this settings in Settings Group
//  - Update ports and things information
//  - Register the widget to factory
// =================================================================================================

// React
import * as React from "react";
import _ from 'lodash';

// Internal components
import { Plegma } from '@API/Api';
import { WebNode } from '@API/WebNode';
import { WebThingPortType } from '@API/WebNodeTypes';
import { WebThingPort, WebThing } from '@API/WebThing';

import { isDefined, IsNullOrEmpty } from '@Utils/Micro';
import * as WidgetsThingsStructs from '@UI/Widgets/Things/WidgetsThingsStructs';
import { IWidgetDataValidateResp } from '@UI/Widgets/IWidgetDataValidateResp';
import { IWidgetThingsClass } from '@UI/Widgets/Things/Utils/IWidgetThingsClass';
import { WidgetDescriptionToPlegmaThing, IWidgetDescription } from '@UI/Widgets/WidgetStructs';

import { LazyLoadingAnimation } from '@Primitive/Lazy';

import { PortKeys, AmChartMapWithTimelineData } from './AmChartMapWithTimelineStructs';
// ------------------------------------------------------------------------------------------------------
const AmChartMapWithTimeline = React.lazy(() => import("./AmChartMapWithTimelineAsync"));
const AmChartMapWithTimelineSettings = React.lazy(() => import("./AmChartMapWithTimelineAsync").then(module => ({ default: module.AmChartMapWithTimelineSettings })));
// =================================================================================================
export class AmChartMapWithTimelineWidget extends IWidgetThingsClass<AmChartMapWithTimelineData> {
    Render(key: string, width: number, height: number): React.ReactNode {
        return <WidgetsThingsStructs.LazyWidget >
            <AmChartMapWithTimeline />
        </WidgetsThingsStructs.LazyWidget>
    }
    // ---------------------------------------------------------------------------------------------
    RenderWidgetSettings(key: string, width: number, height: number): React.ReactElement<any> {
        const parent = this.Parent;
        return <React.Suspense fallback={<LazyLoadingAnimation />}>
            <AmChartMapWithTimelineSettings
                key={'settings-' + key}
                width={width}
                height={height}
                WidgetData={_.cloneDeep(this.WidgetData)}
                WidgetDefaultValues={_.cloneDeep(this.Description.DefaultValues)}
                ThingKey={this.ThingKey}
                NodeKey={this.NodeKey}
                onWidgetDataUpdate={(d) => this.WidgetDataUpdate(d)}
                SettingListToolbar={isDefined(parent) ? parent.SettingsListToolbar : null}
            />
        </React.Suspense>
    }
    // ---------------------------------------------------------------------------------------------
    private createThingPorts(widgetData: AmChartMapWithTimelineData): WebThingPort[] {
        let ports: WebThingPort[] = [];

        // TODO: Set the Ports
        ports.push(new WebThingPort(
            {
                Name: "Data",
                PortKey: PortKeys.Data,
                Type: Plegma.ePortType.JsonString,
                ioDirection: Plegma.ioPortDirection.Input,
                ConfFlags: Plegma.ePortConf.None,
                Description: 'PortKeyDescription',
                State: ''
            },
            WebThingPortType.Data,
            (data) => this.ValidateValues(PortKeys.Data, data))
        );
        return ports;
    }
    // ---------------------------------------------------------------------------------------------
    updateThing() {
        let widgetData = this.WidgetData,
            webNode = WebNode.GetInstanceKey(this.NodeKey),
            webThing = webNode.GetThing(this.ThingKey);

        webThing.Ports = {};

        // Add thing's ports.
        this
            .createThingPorts(widgetData)
            .forEach((port) => webThing.AddPort(port, false));

        // Update thing.
        webNode
            .GetModule(this.WebModuleKey)
            .UpdateThing(webThing);
    }
    // ---------------------------------------------------------------------------------------------
    initThing() {
        let widgetData = (this.WidgetData as AmChartMapWithTimelineData);

        // TOOD: Update thing description and widget icon

        // Create plegma thing.
        let plegmaThing = WidgetDescriptionToPlegmaThing(this.Description);

        // Create thing's ports and web thing.
        let ports = this.createThingPorts(widgetData);
        let webThing = new WebThing(this.WebModule, plegmaThing, ports);

        this.ThingKey = webThing.ThingKey;
    }
    // ---------------------------------------------------------------------------------------------
    public static GetDefaultWidgetDataStatic(): AmChartMapWithTimelineData {
        return {
            // TODO: Set the default values for the widget
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultWidgetData() { return AmChartMapWithTimelineWidget.GetDefaultWidgetDataStatic(); }
    // ---------------------------------------------------------------------------------------------
    public static GetWidgetDesc(): IWidgetDescription {
        // TODO: Update the widget Description
        return {
            Title: 'amChart Map with timeline chart',
            WidgetTitle: '',
            Key: 'AmChartMapWithTimelineWidget',
            Type: 'AmChartMapWithTimelineWidget',
            Data: AmChartMapWithTimelineWidget.GetDefaultWidgetDataStatic(),
            Description: 'AmChartMapWithTimelineWidget Description',
            MinH: 1,
            MinW: 1,
            ShowExtraMenu: false,
            IconURI: WidgetsThingsStructs.IconURIPath + '/svg/tile.svg'
        };
    }
    // ---------------------------------------------------------------------------------------------
    public GetLocalizationDictionary() {
        const res = {};

        // expose title if defined
        if (!IsNullOrEmpty(this.Description.WidgetTitle))
            res[WidgetsThingsStructs.WidgetLocalizationKeys.Title] = this.Description.WidgetTitle;

        return res;
    }
    // ---------------------------------------------------------------------------------------------
    public ValidateValues(key: string, value: any): IWidgetDataValidateResp {
        // todo: set schema validators
        return { valid: true, errors: "" };
    }
    // ---------------------------------------------------------------------------------------------
    public MigrateDefaultWidgetData(data: AmChartMapWithTimelineData): AmChartMapWithTimelineData {
        return data;
    }
    // ---------------------------------------------------------------------------------------------
    public GetDefaultValues() { return {} }
    // ---------------------------------------------------------------------------------------------
}